/* eslint-disable max-params */
/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
/* eslint-disable @typescript-eslint/ban-ts-comment */
/* eslint-disable react/no-unknown-property */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useTableNav } from '@pypestream/table-nav';
import {
  ColumnFiltersState,
  FilterFn,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  SortingFn,
  sortingFns,
  Row,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { debounce } from 'lodash';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import {
  RankingInfo,
  rankItem,
  compareItems,
} from '@tanstack/match-sorter-utils';

import {
  memo,
  useMemo,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  Panel,
  PanelGroup,
  PanelResizeHandle,
  getResizeHandleElement,
} from 'react-resizable-panels';
import {
  useResizeDetector,
  useResizeDetectorProps,
} from 'react-resize-detector';
import {
  Button,
  Icon,
  TextBody,
  Tooltip,
  IconButton,
  Search,
  Divider,
  SearchPropsWithRef,
  TextSubtitle,
  Stack,
} from '@pypestream/design-system';
import {
  DragDropEvents,
  DragDropProvider,
  KeyboardSensor,
  PointerSensor,
} from '@dnd-kit/react';
import { PointerActivationConstraints } from '@dnd-kit/dom';

import { arrayMoveImmutable, calculateSelectedRowsHeight } from '../../utils';
import {
  tableStore,
  TableRow,
  emitOnce,
  TableNode,
  NodeColumns,
  ColumnsWidths,
  type WorkflowError,
} from '../../stores/table-store';
import { NodeAnswerRequiredCheckbox } from './node-answer-required-checkbox';
import { NodeBehaviorsMultiSelect } from './node-behaviors-multi-select';
import { NodeRichAssetSingleSelect } from './node-rich-asset-single-select';
import { NodeCommandSelect } from './node-command-select';
import { TableCellCheckbox } from './editable-table-cell-checkbox';
import { EditableTableCell } from './editable-table-cell-input';
import { EditableTableCellTextArea } from './editable-table-cell-textarea';
import { RichAssetContentCell } from './node-rich-asset-content-cell';
import svgHandles from './icon-handles.svg?react';
import { NodeTypeCell } from './node-type-cell';
import { Cell } from './src/cell';
import { ControlsBar } from './src/controls-bar';
import { DraggableRow } from './src/draggable-row';
import { HeaderCell } from './src/header-cell';
import { SupervisorPanel } from './supervisor/supervisor-panel';
// post-mvp ui
// import { ResourceLibPanel } from './resource-lib/post-mvp/resource-lib-panel';
import { ResourceLibPanel } from './resource-lib/resource-lib-panel';
import { useProStudioCtxSelector } from '../../xstate/app.xstate';
import { columnDefaultSizes } from '../../stores';
import { getCellKey } from './utils';
import './table.scss';

export type ButtonOption = {
  label: string;
  dest: number;
};

const resizeDetectorConfig: useResizeDetectorProps<any> = {
  handleHeight: false,
  refreshMode: 'debounce',
  refreshRate: 50,
};

const TOOLBAR_MIN_SIZE_IN_PIXELS = 41;
const SIDE_BAR_SIZE_IN_PIXELS = 500;
const TOOLBAR_MAX_SIZE_IN_PIXELS = 500;

// Import serializeValue from header-cell or redefine here for consistency
const serializeValue = (value: unknown): string => {
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object' && value !== null) return JSON.stringify(value);
  if (value === undefined || value === null) return '';
  return String(value);
};

// Declare the multiValue filter function type
declare module '@tanstack/table-core' {
  interface FilterFns {
    multiValue: FilterFn<any>;
    fuseFilter: FilterFn<unknown>;
  }
}

declare module '@tanstack/react-table' {
  // add fuzzy filter to the filterFns
  interface FilterFns {
    fuzzy: FilterFn<unknown>;
    fuseFilter: FilterFn<unknown>;
  }
  interface FilterMeta {
    itemRank: RankingInfo;
  }
}

// Multi-value filter function for all columns
const multiValueFilter = (
  row: Row<any>,
  columnId: string,
  filterValue: unknown[]
) => {
  if (!filterValue || filterValue.length === 0) return true;
  const cellValue = row.getValue(columnId);
  const serializedCell = serializeValue(cellValue);
  return filterValue.map(serializeValue).includes(serializedCell);
};

// Define a custom fuzzy filter function that will apply ranking info to rows (using match-sorter utils)
// eslint-disable-next-line max-params
const fuzzyFilter: FilterFn<any> = (row, columnId, value, addMeta) => {
  // Rank the item
  const itemRank = rankItem(row.getValue(columnId), value, {
    threshold: 5,
  });

  // const itemRank = rankItem(row.getValue(columnId), value);

  // Store the itemRank info
  addMeta({
    itemRank,
  });

  // Return if the item should be filtered in/out
  return itemRank.passed;
};

// Define a custom fuzzy sort function that will sort by rank if the row has ranking information
const fuzzySort: SortingFn<any> = (rowA, rowB, columnId) => {
  let dir = 0;

  // Only sort by rank if the column has ranking information
  if (rowA.columnFiltersMeta[columnId]) {
    dir = compareItems(
      rowA.columnFiltersMeta[columnId]?.itemRank!,
      rowB.columnFiltersMeta[columnId]?.itemRank!
    );
  }

  // Provide an alphanumeric fallback for when the item ranks are equal
  return dir === 0 ? sortingFns.alphanumeric(rowA, rowB, columnId) : dir;
};

// Optimized cell components that avoid unnecessary MobX observers
const IndexCell = memo(({ row }: { row: any }) => <span>{row.index + 1}</span>);

const NodeNumberCell = memo(({ row, column }: { row: any; column: any }) => {
  const rowId = row.original.id;
  const { intent, settings } = row.original;
  const columnId = column.id;
  const { draftVersionSolutionData } = useProStudioCtxSelector((ctx) => ({
    draftVersionSolutionData: ctx.draftVersionSolutionData,
  }));
  const isTaskNode =
    settings?.taskId &&
    draftVersionSolutionData?.tasks?.some(
      (task) => task.id === settings.taskId
    );

  return (
    <>
      <EditableTableCell
        rowId={rowId}
        columnId={columnId}
        // minWidth="100%"
        // width="100%"
        type="number"
      />
      {intent === 'out_of_scope' && (
        <Tooltip
          test-id="c-editor-table-td-nlu-entry-tooltip"
          content="Natural Language (NL) Entry"
          offset={2}
        >
          <div className="c-editor-table-td__suffix">
            <Icon name="stars" color="purple" />
          </div>
        </Tooltip>
      )}
      {isTaskNode && (
        <Tooltip
          test-id="c-editor-table-td-task-node-tooltip"
          content="Task Node"
        >
          <div className="c-editor-table-td__suffix">
            <Icon name="target" color="purple" />
          </div>
        </Tooltip>
      )}
    </>
  );
});

const NodeTypeCellComponent = memo(
  ({ row, column }: { row: any; column: any }) => {
    const rowId = row.original.id;

    const index = tableStore.getRowIndex(rowId);

    return (
      <NodeTypeCell
        testId={`c-editor-table--nodeType-${index}-select`}
        rowId={row.original.id}
        columnId={column.id}
      />
    );
  }
);

const NodeNameCell = memo(({ row, column }: { row: any; column: any }) => (
  <EditableTableCell rowId={row.original.id} columnId={column.id} />
));

const NluDisabledCell = memo(({ row, column }: { row: any; column: any }) => (
  <TableCellCheckbox rowId={row.original.id} columnId={column.id} />
));

const NextNodesCell = memo(({ row, column }: { row: any; column: any }) => {
  const rowId = row.original.id;
  const columnId = column.id;

  return (
    <EditableTableCell
      rowId={rowId}
      columnId={columnId}
      onChangeConversion={(input: string) => {
        const stringAsArray = input.split(',').filter(Boolean);

        // if (input && input[input.length - 1] === ',') {
        //   return null;
        // }

        return stringAsArray;
      }}
      renderValue={(arg: string[] | string | number[] | number) => {
        if (typeof arg === 'string') {
          return arg;
        } else if (Array.isArray(arg)) {
          return arg?.join(',') || '';
        } else {
          return '';
        }
      }}
    />
  );
});

const MessageCell = memo(({ row, column }: { row: any; column: any }) => (
  <EditableTableCellTextArea rowId={row.original.id} columnId={column.id} />
));

const UtterancesCell = memo(({ row, column }: { row: any; column: any }) => (
  <EditableTableCellTextArea rowId={row.original.id} columnId={column.id} />
));

const RichAssetTypeCell = memo(({ row, column }: { row: any; column: any }) => (
  <NodeRichAssetSingleSelect
    rowId={row.original.id}
    testId={`c-editor-table--${column.id}-${tableStore.getRowIndex(row.original.id)}-select`}
  />
));

const AnswerRequiredCell = memo(
  ({ row, column }: { row: any; column: any }) => (
    <NodeAnswerRequiredCheckbox
      rowId={row.original.id}
      testId={`c-editor-table--${column.id}-${tableStore.getRowIndex(row.original.id)}-checkbox`}
    />
  )
);

const BehaviorsCell = memo(({ row, column }: { row: any; column: any }) => {
  const rowId = row.original.id;
  const columnId = column.id;

  return (
    <NodeBehaviorsMultiSelect
      rowId={rowId}
      columnId={columnId}
      onEdit={(values: string[] | undefined) => {
        // Dropdowns should save immediately
        tableStore.updateRow({
          rowId,
          updates: {
            [columnId]: values,
          },
          skipValidationRules: false,
          saveImmediately: true,
        });
      }}
    />
  );
});

const CommandCell = memo(({ row, column }: { row: any; column: any }) => {
  return (
    <NodeCommandSelect
      rowId={row.original.id}
      testId={`c-editor-table--${column.id}-${tableStore.getRowIndex(row.original.id)}-select`}
    />
  );
});

const OutputCell = memo(({ row, column }: { row: any; column: any }) => {
  const rowId = row.original.id;
  const columnId = column.id;

  return (
    <EditableTableCell
      rowId={rowId}
      columnId={columnId}
      onChangeConversion={(input: string) => {
        const stringAsArray = input.split(',');

        // if (input && input[input.length - 1] === ',') {
        //   return null;
        // }

        return stringAsArray;
      }}
      renderValue={(arg: string[] | string | number[] | number) => {
        if (typeof arg === 'string') {
          return arg;
        } else if (Array.isArray(arg)) {
          return arg?.join(',') || '';
        } else {
          return '';
        }
      }}
    />
  );
});

const JsonInputCell = memo(({ row, column }: { row: any; column: any }) => {
  const rowId = row.original.id;
  const columnId = column.id;
  const isDisabled = tableStore.isCellDisabled(rowId, columnId);

  return (
    <EditableTableCellTextArea
      rowId={rowId}
      columnId={columnId}
      index={tableStore.getRowIndex(rowId)}
      disabled={isDisabled}
    />
  );
});

const WhatsNextCell = memo(({ row, column }: { row: any; column: any }) => {
  const rowId = row.original.id;
  const columnId = column.id;

  return (
    <EditableTableCell
      rowId={rowId}
      columnId={columnId}
      onChangeConversion={(input: string) => {
        const stringAsArray = input.split('|');

        return stringAsArray;
      }}
      renderValue={(arg: string[] | string | number[] | number) => {
        if (typeof arg === 'string') {
          return arg;
        } else if (Array.isArray(arg)) {
          return arg?.join('|') || '';
        } else {
          return '';
        }
      }}
    />
  );
});

const VariablesCell = memo(({ row, column }: { row: any; column: any }) => {
  const rowId = row.original.id;
  const columnId = column.id;

  return (
    <EditableTableCell
      rowId={rowId}
      columnId={columnId}
      onChangeConversion={(input: string) => {
        const stringAsArray = input.split(',');
        // .filter(Boolean);

        // if (input && input[input.length - 1] === ',') {
        //   return null;
        // }
        return stringAsArray;
      }}
      renderValue={(arg: string[] | string | number[] | number) => {
        if (typeof arg === 'string') {
          return arg;
        } else if (Array.isArray(arg)) {
          return arg?.join(',') || '';
        } else {
          return '';
        }
      }}
    />
  );
});

export const Table: React.FC<{
  projectName?: string;
  botName?: string;
}> = observer(() => {
  const { width: indexCellWidth, ref: indexCellRef } =
    useResizeDetector(resizeDetectorConfig);

  const { isDebugMode, resourceLibraryData } = useProStudioCtxSelector(
    (ctx) => ({
      isDebugMode: ctx.debugMode,
      resourceLibraryData: ctx.resourceLibraryData,
    })
  );

  const tableRef = useRef<HTMLTableElement>(null);
  const resizeHandleRef = useRef<HTMLElement>(undefined);
  const cellRefs = useRef(new Map<string, HTMLTableCellElement | null>());
  const rowRefs = useRef(new Map<string, HTMLTableRowElement | null>());
  const workflowsErrors = tableStore.errors;

  // Precompute cell errors map and row error set once when errors change (O(errors) instead of O(rows*cols) per-cell find)
  const { cellErrorsMap, rowNodeNumbersWithErrors } = useMemo(() => {
    const map = new Map<string, WorkflowError>();
    const rowSet = new Set<number | null>();
    workflowsErrors.forEach((err) => {
      const key = `${err.nodeNumber ?? 'null'}-${String(err.colId)}`;
      map.set(key, err);
      rowSet.add(err.nodeNumber);
    });
    return { cellErrorsMap: map, rowNodeNumbersWithErrors: rowSet };
  }, [workflowsErrors]);

  // const [sidebarOpen, showSidebar] = useState(false);
  // const [addEmbedsPanelOpen, showEmbedsPanel] = useState(false);
  const [supervisorPanelOpen, showSupervisorPanel] = useState(false);
  const [toolbarMinSize, setToolbarMinSize] = useState(5);
  const [toolbarMaxSize, setToolbarMaxSize] = useState(100);
  const [sidebarSize, setSidebarSize] = useState(30);
  const [resourceLibPanelOpen, showResourceLibPanel] = useState(false);

  // Row resize state management
  const [resizingRowId, setResizingRowId] = useState<string | null>(null);
  const [resizeHeight, setResizeHeight] = useState<number | null>(null);
  const resizeRowInitialTopRef = useRef<number | null>(null);
  const resizeHeightRef = useRef<number | null>(null);
  const MIN_ROW_HEIGHT = 32;

  useLayoutEffect(() => {
    const containerPanelGroup = document.querySelector(
      '[data-panel-group-id="container"]'
    ) as Element;

    const panelGroup = document.querySelector(
      '[data-panel-group-id="layout"]'
    ) as Element;

    const resizeHandles = document.querySelectorAll(
      '[data-panel-resize-handle-id]'
    ) as NodeListOf<Element>;

    const resizeObserver = new ResizeObserver(() => {
      const width = (containerPanelGroup as HTMLElement).offsetWidth;
      let height = (panelGroup as HTMLElement).offsetHeight;

      resizeHandles.forEach((resizeHandle) => {
        height -= (resizeHandle as HTMLElement).offsetHeight;
      });

      // Minimum size in pixels is a percentage of the PanelGroup's height,
      // less the (fixed) height of the resize handles.
      setToolbarMinSize((TOOLBAR_MIN_SIZE_IN_PIXELS / height) * 100);
      setToolbarMaxSize((TOOLBAR_MAX_SIZE_IN_PIXELS / height) * 100);
      setSidebarSize((SIDE_BAR_SIZE_IN_PIXELS / width) * 100);
    });

    if (panelGroup) {
      resizeObserver.observe(panelGroup);
    }

    if (resizeHandles) {
      resizeHandles.forEach((resizeHandle) => {
        resizeObserver.observe(resizeHandle);
      });
    }

    return () => {
      resizeObserver.disconnect();
    };
  });

  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);

  const [sorting, setSorting] = useState<SortingState>([]);
  const { listeners, tableNav } = useTableNav({ debug: true });

  // Read so observer re-renders when set from ValidationPopover (navigate from other page)
  const { pendingScrollToCell } = tableStore;

  useEffect(() => {
    tableStore.setScrollToCellCallback((el) => tableNav.scrollToCell(el));
    return () => tableStore.setScrollToCellCallback(null);
  }, [tableNav]);

  useEffect(() => {
    const pending = pendingScrollToCell;
    if (!pending) return;
    tableStore.scrollToAndFocusCell(pending.rowIndex, pending.columnId);
    tableStore.clearPendingScrollToCell();
  }, [pendingScrollToCell]);

  const { currentPage } = tableStore;

  useEffect(() => {
    if (!tableStore.pendingRefocusAfterPageChange) return;
    tableStore.pendingRefocusAfterPageChange = false;
    tableStore.doRefocusAfterPageChange();
  }, [currentPage]);

  useEffect(() => {
    // todo: refactor out to use a CSS classname based on the route when React Router gets upgraded
    document.documentElement.style.overscrollBehaviorX = 'contain';
    document.body.style.overscrollBehaviorX = 'contain';

    return () => {
      document.documentElement.style.removeProperty('overscroll-behavior-x');
      document.body.style.removeProperty('overscroll-behavior-x');
    };
  }, []);

  useEffect(() => {
    const resizeHandleElement = getResizeHandleElement('table-resizer');

    if (resizeHandleElement instanceof HTMLElement) {
      resizeHandleRef.current = resizeHandleElement;
    }
  }, []);

  useEffect(() => {
    // todo: refactor out to use a CSS classname based on the route when React Router gets upgraded
    const tableBodyHandler = (event: MouseEvent) => {
      if (!tableRef.current) return;

      if (event.target instanceof Element) {
        if (
          (resizeHandleRef.current &&
            event.target?.contains(resizeHandleRef.current)) ||
          (tableRef.current &&
            event.target?.contains(
              tableRef.current?.querySelector('.c-editor-table-td--index-cell')
            ))
        ) {
          return;
        }

        if (
          event.target?.contains(tableRef.current) ||
          event.target?.closest('ps-universal-nav') ||
          event.target?.closest('.c-editor-table-info')
        ) {
          tableStore.deselectAllCells();
          tableStore.clearSelectedStartCell();
          tableStore.deselectAllRows();
        }
      }
    };
    document.body.addEventListener('click', tableBodyHandler);

    return () => {
      document.body.removeEventListener('click', tableBodyHandler);
    };
  }, []);

  // Update ref whenever resizeHeight changes (for display purposes)
  useEffect(() => {
    resizeHeightRef.current = resizeHeight;
  }, [resizeHeight]);

  // Centralized row resize listeners - only one set of listeners for all rows
  useEffect(() => {
    if (!resizingRowId || resizeRowInitialTopRef.current === null) return;

    const handleMouseMove = (e: MouseEvent) => {
      const newHeight = Math.max(
        MIN_ROW_HEIGHT,
        e.clientY - resizeRowInitialTopRef.current!
      );
      resizeHeightRef.current = newHeight;
      setResizeHeight(newHeight);
    };

    const handleMouseUp = () => {
      if (resizingRowId && resizeHeightRef.current !== null) {
        const { selectedRows } = tableStore;
        if (selectedRows.size > 1 && selectedRows.has(resizingRowId)) {
          [...selectedRows.values()].map((value) => {
            // Save row height to table store settings using ref to get latest value
            tableStore.updateRow({
              rowId: value,
              updates: {
                settings: {
                  ...tableStore.getValue(value, NodeColumns.settings),
                  rowHeight: resizeHeightRef.current,
                },
              },
              skipValidationRules: true,
              saveImmediately: true,
            });
          });
        } else {
          // Save row height to table store settings using ref to get latest value
          tableStore.updateRow({
            rowId: resizingRowId,
            updates: {
              settings: {
                ...tableStore.getValue(resizingRowId, NodeColumns.settings),
                rowHeight: resizeHeightRef.current,
              },
            },
            skipValidationRules: true,
            saveImmediately: true,
          });
        }
      }
      setResizingRowId(null);
      setResizeHeight(null);
      resizeRowInitialTopRef.current = null;
      resizeHeightRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizingRowId]);

  // useEffect(() => {
  //   if (
  //     tableStore.currentState !== 'loading' &&
  //     tableStore.hasPendingRowsToRender
  //   ) {
  //     requestIdleCallback(() => {
  //       tableStore.loadPendingRows();
  //     });
  //   }
  // }, [hasPendingRowsToRender]);

  // Warn user about unsaved changes before leaving the page
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (tableStore.hasUnsavedChanges) {
        // Modern browsers require returnValue to be set
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
        return ''; // For older browsers
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  const columns = [
    // 134mb
    {
      id: 'index',
      header: 'Index',
      cell: IndexCell,
      size: indexCellWidth && indexCellWidth >= 42 ? indexCellWidth : 42,
      minSize: indexCellWidth && indexCellWidth >= 42 ? indexCellWidth : 42,
      maxSize: indexCellWidth && indexCellWidth >= 42 ? indexCellWidth : 42,
      enableColumnFilter: true,
      filterFn: multiValueFilter,
    },
    // {
    //   accessorKey: 'pinned',
    //   id: 'pinned',
    //   header: 'Pinned',
    //   cell: ({ row }: { row: Row<TableRow> }) => (
    //     <button
    //       onClick={() => {
    //         if (row.getIsPinned()) {
    //           runInAction(() => {
    //             tableStore.getRowById(row.original.id).pinned = false;
    //           });
    //           row.pin(false);
    //         } else {
    //           runInAction(() => {
    //             tableStore.getRowById(row.original.id).pinned = true;
    //           });
    //           row.pin('top');
    //         }
    //       }}
    //     >
    //       {row.getIsPinned() ? '❌' : '📌'}
    //     </button>
    //   ),
    //   minSize: 84,
    //   maxSize: 84,
    //   // hidden: !isDebugMode,
    //   // enableColumnFilter: true,
    //   // filterFn: multiValueFilter,
    // },
    {
      accessorKey: 'order',
      id: 'order',
      header: 'Order',
      cell: ({ row }: { row: Row<TableRow> }) => (
        <span>{row.original.order}</span>
      ),
      minSize: 84,
      maxSize: 84,
      // Debug column to show order values - only in development
      hidden: !isDebugMode,
      enableColumnFilter: true,
      filterFn: multiValueFilter,
    },
    // 220mb
    {
      accessorKey: NodeColumns.nodeNumber,
      id: NodeColumns.nodeNumber,
      header: 'Node #',
      maxSize: 200,
      cell: NodeNumberCell,
      enableColumnFilter: true,
      filterFn: multiValueFilter,
    },
    // 230mb
    {
      accessorKey: NodeColumns.nodeType,
      id: NodeColumns.nodeType,
      header: 'Node Type',
      minSize: 74,
      cell: NodeTypeCellComponent,
      enableColumnFilter: true,
      filterFn: multiValueFilter,
    },
    // 225mb
    {
      accessorKey: NodeColumns.nodeName,
      id: NodeColumns.nodeName,
      header: 'Node Name',
      cell: NodeNameCell,
      enableColumnFilter: true,
      filterFn: multiValueFilter,
      enableSorting: true,
      sortingFn: fuzzySort,
    },
    {
      accessorKey: NodeColumns.utterances,
      id: NodeColumns.utterances,
      header: 'Example Phrases',
      cell: UtterancesCell,
      enableColumnFilter: true,
      filterFn: multiValueFilter,
    },
    // 327mb
    {
      accessorKey: NodeColumns.message,
      id: NodeColumns.message,
      header: 'Message',
      cell: MessageCell,
      minSize: 160,
      maxSize: 700,
      enableColumnFilter: true,
      filterFn: multiValueFilter,
    },
    // 376mb
    // {
    //   accessorKey: 'embed',
    //   header: 'Embed',
    //   cell: (cell) => (
    //     <EmbedsCell
    //       index={cell.row.index + 1}
    //       onShow={handleEmbedHeaderClick}
    //       onAddEmbed={() => showEmbedsPanel(true)}
    //     />
    //   ),
    //   size: 424,
    // },
    {
      accessorKey: NodeColumns.richAssetType,
      id: NodeColumns.richAssetType,
      header: 'Asset Type',
      cell: RichAssetTypeCell,
      minSize: 148,
      enableColumnFilter: true,
      filterFn: multiValueFilter,
    },
    // 415mb
    {
      accessorKey: NodeColumns.richAssetContent,
      id: NodeColumns.richAssetContent,
      header: 'Asset Content',
      cell: RichAssetContentCell,
      maxSize: 600,
      minSize: 140,
      enableColumnFilter: true,
      filterFn: multiValueFilter,
    },
    // 450mb
    {
      accessorKey: NodeColumns.behaviors,
      id: NodeColumns.behaviors,
      header: 'Behaviors',
      cell: BehaviorsCell,
      minSize: 235,
      enableColumnFilter: true,
      filterFn: multiValueFilter,
    },
    // 431mb
    {
      accessorKey: NodeColumns.answerRequired,
      id: NodeColumns.answerRequired,
      header: 'Ans Req?',
      cell: AnswerRequiredCell,
      enableColumnFilter: true,
      filterFn: multiValueFilter,
    },
    // 257mb
    {
      accessorKey: NodeColumns.nluDisabled,
      id: NodeColumns.nluDisabled,
      header: 'NLU Disabled?',
      cell: NluDisabledCell,
      maxSize: 200,
      enableColumnFilter: true,
      filterFn: multiValueFilter,
    },
    // 280mb
    {
      accessorKey: NodeColumns.nextNodes,
      id: NodeColumns.nextNodes,
      header: 'Next Nodes',
      cell: NextNodesCell,
      maxSize: 200,
      enableColumnFilter: true,
      filterFn: multiValueFilter,
    },
    {
      accessorKey: NodeColumns.cssClassname,
      id: NodeColumns.cssClassname,
      header: 'CSS Classname',
      enableColumnFilter: true,
      filterFn: multiValueFilter,
    },
    // 458mb
    {
      accessorKey: NodeColumns.command,
      id: NodeColumns.command,
      header: 'Command',
      cell: CommandCell,
      minSize: 130,
      enableColumnFilter: true,
      filterFn: multiValueFilter,
    },
    // 489mb
    {
      accessorKey: NodeColumns.description,
      id: NodeColumns.description,
      header: 'Description',
      minSize: 130,
      enableColumnFilter: true,
      filterFn: multiValueFilter,
    },
    // 498mb -- possible memory leak starts
    {
      accessorKey: NodeColumns.output,
      id: NodeColumns.output,
      header: 'Output',
      cell: OutputCell,
      enableColumnFilter: true,
      filterFn: multiValueFilter,
    },
    // 515mb
    {
      accessorKey: NodeColumns.nodeInput,
      id: NodeColumns.nodeInput,
      header: 'Node Input',
      minSize: 93,
      enableColumnFilter: true,
      filterFn: multiValueFilter,
    },
    // 530mb
    {
      accessorKey: NodeColumns.parameterInput,
      id: NodeColumns.parameterInput,
      header: 'Parameter Input',
      cell: JsonInputCell,
      minSize: 280,
      enableColumnFilter: true,
      filterFn: multiValueFilter,
    },
    // 550mb
    {
      accessorKey: NodeColumns.decisionVariable,
      id: NodeColumns.decisionVariable,
      header: 'Decision Variable',
      minSize: 127,
      enableColumnFilter: true,
      filterFn: multiValueFilter,
    },
    // 570mb -- possible memory leak end
    {
      accessorKey: NodeColumns.whatsNext,
      id: NodeColumns.whatsNext,
      header: 'What Next?',
      cell: WhatsNextCell,
      enableColumnFilter: true,
      filterFn: multiValueFilter,
    },
    // 635mb
    {
      accessorKey: NodeColumns.variables,
      id: NodeColumns.variables,
      header: 'Variables',
      cell: VariablesCell,
      enableColumnFilter: true,
      filterFn: multiValueFilter,
    },
    // 615 mb
    {
      accessorKey: NodeColumns.skillTag,
      id: NodeColumns.skillTag,
      header: 'Skill Tag',
      enableColumnFilter: true,
      filterFn: multiValueFilter,
    },
    // 653mb
    {
      accessorKey: NodeColumns.analyticsEvent,
      id: NodeColumns.analyticsEvent,
      header: 'Analytics Events',
      cell: JsonInputCell,
      enableColumnFilter: true,
      filterFn: multiValueFilter,
    },
    {
      accessorKey: NodeColumns.nodeTags,
      id: NodeColumns.nodeTags,
      header: 'Node Tags',
      enableColumnFilter: true,
      filterFn: multiValueFilter,
    },
    {
      accessorKey: NodeColumns.platformFlag,
      id: NodeColumns.platformFlag,
      header: 'Platform Flag',
      minSize: 104,
      enableColumnFilter: true,
      filterFn: multiValueFilter,
    },
  ].map((col) => {
    if (col.id === 'index') {
      return col;
    }

    return {
      ...col,
      size:
        tableStore.columnsSizes?.[col.id as keyof ColumnsWidths] || col.size,
      minSize: col.minSize || columnDefaultSizes.minWidth,
      maxSize: col.maxSize || columnDefaultSizes.maxWidth,
    };
  });

  const [globalFilter, setGlobalFilter] = useState('');

  // useEffect(() => {
  //   tableStore.search(globalFilter);
  // }, [globalFilter]);

  // Define the custom filter function
  const fuseFilterFn: FilterFn<any> = (
    row: Row<any>,
    _columnId: string,
    filterValue: any
  ) => {
    const searchPattern = String(filterValue ?? '').trim();
    if (searchPattern === '') return true;
    return tableStore.searchResultIds.has(row.original.id);
  };

  const table = useReactTable<TableRow>({
    data: tableStore.sortedRows,
    columns,
    defaultColumn: {
      cell: NodeNameCell,
      size: 150,
      minSize: 50,
      maxSize: 500,
    },
    state: {
      columnPinning: tableStore.columnPinningSettings,
      columnFilters,
      sorting,
      globalFilter,
      pagination: {
        pageIndex: tableStore.currentPage,
        pageSize: tableStore.paginationSize,
      },
    },
    // manualPagination: true,
    autoResetPageIndex: false,
    // rowCount: tableStore.sortedRows.length,
    enableRowSelection: true,
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    globalFilterFn: fuseFilterFn,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: (updater) => {
      if (typeof updater !== 'function') return;

      const newPageInfo = updater(table.getState().pagination);

      const newPageIndex = newPageInfo.pageIndex;
      const newPageSize = newPageInfo.pageSize;

      tableStore.currentPage = newPageIndex;
      tableStore.paginationSize = newPageSize;
    },
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    getSortedRowModel: getSortedRowModel(),
    onColumnPinningChange: (updater) =>
      tableStore.setColumnPinningSettings(updater),
    columnResizeMode: 'onChange',
    filterFns: {
      multiValue: multiValueFilter,
      fuzzy: fuzzyFilter,
      fuseFilter: fuseFilterFn,
    },
  });

  useEffect(() => {
    if (table) {
      tableStore.table = table;
    }

    if (cellRefs.current) {
      tableStore.cellRefs = cellRefs;
    }

    if (rowRefs.current) {
      tableStore.rowRefs = rowRefs;
    }

    return () => {
      if (tableStore.dragSession) {
        tableStore.endDragSession();
      }
      if (tableStore.rowRangeSelection) {
        tableStore.endRowRangeSelection();
      }
    };
  }, [table, cellRefs, rowRefs]);

  //
  const handleDragEnd: DragDropEvents['dragend'] = (event) => {
    // const { active, over } = event;
    const { target } = event.operation;
    const sourceRowId = tableStore.dragSession?.activeRowId;
    const selectedRowIds = tableStore.dragSession?.selectedRowIds ?? [];
    const isMultiRowDrag =
      selectedRowIds.length > 1 && selectedRowIds.includes(sourceRowId || '');

    // if (active.id !== over?.id) {
    // if (!isSortable(event.operation.source)) return;

    // let isSorted: boolean = false;

    // runInAction(() => {
    // @todo: re-enable and keep working on getting drag and drop working with sorting

    // table.getAllColumns().forEach((column) => {
    //   if (column.getIsSorted()) {
    //     isSorted = true;
    //   }
    // });

    // if (isSorted !== false) {
    //   table.getSortedRowModel().rows.map((row, index) => {
    //     const rowIndex = tableStore.getRowIndex(row.original.id);
    //     tableStore.rows[rowIndex].order = index;
    //   });

    //   table.getAllColumns().map((column) => {
    //     column.clearSorting();
    //     column.toggleSorting(false);
    //   });
    // }

    if (isMultiRowDrag) {
      const insertionIndex = tableStore.dragSession?.insertionIndex;

      if (
        insertionIndex === null ||
        insertionIndex === undefined ||
        !sourceRowId
      ) {
        tableStore.endDragSession();

        return;
      }

      const sortedRows = tableStore
        .getAllSortedRows()
        .filter((row) => row.hidden !== true);

      const selectedIndices = selectedRowIds
        .map((id) => sortedRows.findIndex((row) => row.id === id))
        .filter((idx) => idx !== -1)
        .sort((a, b) => a - b);

      const rowsToMove = selectedIndices.map((idx) => sortedRows[idx]);
      const selectedIdSet = new Set(selectedRowIds);
      const remainingRows = sortedRows.filter(
        (row) => !selectedIdSet.has(row.id)
      );

      let adjustedInsertIndex = insertionIndex;
      for (const idx of selectedIndices) {
        if (idx < insertionIndex) {
          adjustedInsertIndex--;
        }
      }
      adjustedInsertIndex = Math.max(
        0,
        Math.min(adjustedInsertIndex, remainingRows.length)
      );

      // if (tableStore.currentPage !== 0) {
      //   adjustedInsertIndex -=
      //     tableStore.currentPage * tableStore.paginationSize;
      // }

      const result = [
        ...remainingRows.slice(0, adjustedInsertIndex),
        ...rowsToMove,
        ...remainingRows.slice(adjustedInsertIndex),
      ];

      emitOnce(() => {
        for (let i = 0; i < result.length; i++) {
          const item = result[i];
          const newOrder = tableStore.orderIncrementV2 * (i + 1);
          if (item.order !== newOrder) {
            tableStore.updateRow({
              rowId: item.id,
              updates: { order: newOrder },
              skipValidationRules: true,
              saveImmediately: true,
            });
          }
        }

        const allRowOrders = [...toJS(tableStore.sortedRows)].map(
          (row) => row.order
        );
        const hasDuplicates = allRowOrders.some(
          (e, i, arr) => arr.indexOf(e) !== i
        );

        if (hasDuplicates) {
          tableStore.normalizeRowOrder();
        }
      });
    } else {
      let newIndex = -1;
      if (target && 'sortable' in target && target.sortable) {
        newIndex = (target.sortable as { index?: number }).index ?? -1;
      }

      if (tableStore.currentPage !== 0) {
        newIndex += tableStore.currentPage * tableStore.paginationSize;
      }

      const sortedRows = [...tableStore.sortedRows];
      const oldIndex = sortedRows.findIndex((row) => row.id === sourceRowId);

      if (newIndex === -1 || oldIndex === -1 || oldIndex === newIndex) {
        const wasMultiDrag = tableStore.dragSession?.isMulti ?? false;
        tableStore.endDragSession();
        if (!wasMultiDrag) {
          tableStore.deselectAllRows();
        }
        return;
      }

      const startIndex = Math.min(oldIndex, newIndex);
      const endIndex = Math.max(oldIndex, newIndex);

      /**
       * sort the entire array (for accuracy) but only iterate through the subset that's changed
       */

      // the new (reordered) subset of the array
      const result = arrayMoveImmutable<TableNode>(
        sortedRows,
        oldIndex,
        newIndex
      );

      emitOnce(() => {
        for (let i = startIndex; i <= endIndex; i++) {
          const item = result[i];
          const newOrder = tableStore.orderIncrementV2 * (i + 1);
          if (item.order !== newOrder) {
            tableStore.updateRow({
              rowId: item.id,
              updates: {
                order: newOrder,
              },
              skipValidationRules: true,
              saveImmediately: true, // drag & drop should save
            });
          }
        }

        const allRowOrders = [...toJS(tableStore.sortedRows)].map(
          (row) => row.order
        );

        const hasDuplicates = allRowOrders.some(
          (e, i, arr) => arr.indexOf(e) !== i
        );

        // backup check to make sure no two rows have the same order
        if (hasDuplicates) {
          tableStore.normalizeRowOrder();
        }
      });
    }

    const wasMultiDrag = tableStore.dragSession?.isMulti ?? false;
    tableStore.endDragSession();

    if (!wasMultiDrag) {
      tableStore.deselectAllRows();
    }
  };

  const handleDragStart: DragDropEvents['dragstart'] = (event) => {
    const sourceRowId = (
      event.operation.source as { sortable?: { id?: string } }
    )?.sortable?.id;

    if (!sourceRowId) {
      return;
    }

    const isSelected = tableStore.selectedRows.has(sourceRowId);

    const canMultiDrag =
      isSelected &&
      tableStore.selectedRows.size > 1 &&
      tableStore.isSelectionContinuous &&
      !tableStore.isMetaKeyPressed;

    const selectedIds = canMultiDrag
      ? [...tableStore.selectedRows.values()]
      : [sourceRowId];
    const totalHeight = canMultiDrag
      ? calculateSelectedRowsHeight(selectedIds, rowRefs.current)
      : 0;

    let initialTop = 0;
    let initialLeft = 0;
    let initialWidth = 0;
    const sourceRowEl = rowRefs.current.get(sourceRowId);
    if (sourceRowEl) {
      const rect = sourceRowEl.getBoundingClientRect();
      initialTop = rect.top;
      initialLeft = rect.left;
      initialWidth = rect.width;
    }

    const scrollContainer = tableScrollerRef.current;
    const viewportHeight = scrollContainer?.clientHeight ?? window.innerHeight;

    tableStore.startDragSession({
      activeRowId: sourceRowId,
      selectedRowIds: selectedIds,
      groupHeight: totalHeight,
      initialTop,
      initialLeft,
      initialWidth,
      viewportHeight,
    });

    if (tableStore.isOversizeDrag && canMultiDrag && scrollContainer) {
      const { sortedRows } = tableStore;
      for (let i = 0; i < sortedRows.length; i++) {
        if (selectedIds.includes(sortedRows[i].id)) {
          const firstRowEl = rowRefs.current.get(sortedRows[i].id);
          if (firstRowEl) {
            const containerRect = scrollContainer.getBoundingClientRect();
            const rowRect = firstRowEl.getBoundingClientRect();
            const headerOffset = 30;
            const scrollOffset =
              rowRect.top -
              containerRect.top +
              scrollContainer.scrollTop -
              headerOffset;
            scrollContainer.scrollTop = Math.max(0, scrollOffset);
          }
          break;
        }
      }
    }

    emitOnce(() => {
      tableStore.deselectAllCells();
      if (!tableStore.isOversizeDrag && (!isSelected || !canMultiDrag)) {
        tableStore.deselectAllRows();
        tableStore.selectRow(sourceRowId);
      }
    });
  };

  // Compute insertion index from cursor position (only for multi-drag)
  const handleDragMove: DragDropEvents['dragmove'] = (event) => {
    if (!tableStore.dragSession?.isMulti) {
      return;
    }

    const pointerY = (event as any).operation?.position?.current?.y;
    if (pointerY == null) return;

    const rows = tableStore
      .getAllSortedRows()
      .filter((row) => row.hidden !== true);
    let insertionIndex: number | null = null;

    for (let i = 0; i < rows.length; i++) {
      const rowEl = rowRefs.current.get(rows[i].id);
      if (!rowEl) continue;

      const rect = rowEl.getBoundingClientRect();
      const midpoint = rect.top + rect.height / 2;

      if (pointerY < midpoint) {
        insertionIndex = i;
        break;
      } else if (i === rows.length - 1) {
        insertionIndex = i + 1;
      }
    }

    const selectedIds = tableStore.dragSession?.selectedRowIds || [];
    if (insertionIndex !== null) {
      const selectedIndices = selectedIds
        .map((id) => rows.findIndex((r) => r.id === id))
        .filter((i) => i !== -1)
        .sort((a, b) => a - b);

      if (selectedIndices.length > 0) {
        const minSelected = selectedIndices[0];
        const maxSelected = selectedIndices[selectedIndices.length - 1];

        if (insertionIndex > minSelected && insertionIndex <= maxSelected + 1) {
          insertionIndex = null;
        }
      }
    }

    // if (insertionIndex !== null) {
    //   insertionIndex += tableStore.currentPage * tableStore.paginationSize;
    // }

    tableStore.updateInsertionIndex(insertionIndex);
  };

  const handleResizeStart = (rowId: string) => {
    const rowElement = rowRefs.current?.get(rowId);
    if (!rowElement) return;

    const rect = rowElement.getBoundingClientRect();
    resizeRowInitialTopRef.current = rect.top;

    // Get current row height from store or use default
    const currentSettings = tableStore.getValue(rowId, NodeColumns.settings);
    const currentHeight = currentSettings?.rowHeight || rowElement.offsetHeight;

    setResizingRowId(rowId);
    setResizeHeight(currentHeight);
    resizeHeightRef.current = currentHeight;
  };

  const handleResetSize = (rowId: string) => {
    const { selectedRows } = tableStore;
    if (selectedRows.size > 1 && selectedRows.has(rowId)) {
      [...selectedRows.values()].map((value) => {
        // Save row height to table store settings using ref to get latest value
        tableStore.updateRow({
          rowId: value,
          updates: {
            settings: {
              ...tableStore.getValue(value, NodeColumns.settings),
              rowHeight: MIN_ROW_HEIGHT,
            },
          },
          skipValidationRules: true,
          saveImmediately: true,
        });
      });
    } else {
      tableStore.updateRow({
        rowId,
        updates: {
          settings: {
            ...tableStore.getValue(rowId, NodeColumns.settings),
            rowHeight: MIN_ROW_HEIGHT,
          },
        },
        skipValidationRules: true,
        saveImmediately: true,
      });
    }
  };

  const tableScrollerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (tableScrollerRef.current && !tableStore.tableScrollerRef) {
      tableStore.tableScrollerRef = tableScrollerRef.current;
    }
  });

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for common navigation keys like Tab, arrow keys, Enter
      // Enter is included so that when table-nav moves focus to the cell below,
      // the new cell's handleFocus sees isKeyboardNav=true and sets selection (is-selection-start).
      if (
        e.key === 'Tab' ||
        e.key === 'Enter' ||
        e.key === 'ArrowUp' ||
        e.key === 'ArrowDown' ||
        e.key === 'ArrowLeft' ||
        e.key === 'ArrowRight'
      ) {
        tableStore.setKeyboardNav(true);
      }

      if (e.key === 'Tab' && tableStore.isCellBeingEdited) {
        e.preventDefault();

        const activeEl = document.activeElement as HTMLElement;
        const currentTd = activeEl?.closest('td');

        if (currentTd) {
          activeEl?.blur();
          tableStore.isCellBeingEdited = false;

          const allTds = Array.from(
            currentTd.closest('tbody')?.querySelectorAll('td[data-row-id]') ||
              []
          );
          const currentIndex = allTds.indexOf(currentTd);
          const nextTd = e.shiftKey
            ? allTds[currentIndex - 1]
            : allTds[currentIndex + 1];

          if (nextTd instanceof HTMLTableCellElement) {
            nextTd.focus();
            tableStore.selectCellViaKeyboardNavigation(false);
          }
        }
      }

      // Start editing on printable key when a cell is selected but not being edited
      if (
        !tableStore.isCellBeingEdited &&
        tableStore.selectedCells.length === 1 &&
        tableStore.isEditing() &&
        e.key.length === 1 &&
        !e.metaKey &&
        !e.ctrlKey &&
        !e.altKey
      ) {
        e.preventDefault();
        e.stopPropagation();
        tableStore.startCellEditingWithChar(e.key);
      }
    };

    const handleMouseDown = () => {
      tableStore.setKeyboardNav(false);
    };

    // Use capture phase so this runs before table-nav's keydown on the table.
    // That way when Enter moves focus to the cell below, the new cell's onFocus
    // already sees isKeyboardNav=true and applies selection (selectCell + setSelectedStartCell).
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('mousedown', handleMouseDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, []);

  const globalSearch = useRef<SearchPropsWithRef>(undefined);

  useEffect(() => {
    const handleGlobalSearchKeyDown = (e: KeyboardEvent) => {
      if (
        e.keyCode === 114 ||
        (e.ctrlKey && e.keyCode === 70) ||
        (e.metaKey && e.keyCode === 70)
      ) {
        const globalSearchElement =
          globalSearch.current as unknown as HTMLInputElement;

        if (
          globalSearch.current &&
          globalSearchElement !== document.activeElement
        ) {
          e.preventDefault();
          globalSearchElement?.focus();
        } else {
          return true;
        }
      }
    };

    document.addEventListener('keydown', handleGlobalSearchKeyDown);

    return () => {
      document.removeEventListener('keydown', handleGlobalSearchKeyDown);
    };
  }, []);

  // const handleSelectionMouseDown =
  //   (rowId: string, columnId: string) => (e: React.MouseEvent) => {
  //     // Only start selection if clicking the cell background, not its contents
  //     if (e.target === e.currentTarget) {
  //       e.preventDefault();
  //       const selection = { rowId, columnId };
  //       tableStore.setSelectionStart(selection);
  //       tableStore.setSelectedCells([selection]);
  //     }
  //   };

  // const handleSelectionMouseEnter = (rowId: string, columnId: string) => () => {
  //   if (tableStore.selectionStart) {
  //     const selections = tableStore.getCellsBetween(tableStore.selectionStart, {
  //       rowId,
  //       columnId,
  //     });
  //     tableStore.setSelectedCells(selections);
  //   }
  // };

  // Add keyboard event listener for delete key
  // React.useEffect(() => {
  //   const handleKeyDown = (e: KeyboardEvent) => {
  //     if (
  //       e.key === 'Delete' ||
  //       (e.key === 'Backspace' && tableStore.selectedCells.length > 0)
  //     ) {
  //       tableStore.clearSelectedCells();
  //     }
  //   };

  //   window.addEventListener('keydown', handleKeyDown);
  //   return () => window.removeEventListener('keydown', handleKeyDown);
  // }, []);

  return (
    <>
      {/* <div style={{ display: 'inline-block', marginLeft: '1rem' }}>
        <button
          onClick={() => tableStore.undo()}
          disabled={!tableStore.canUndo}
          title="Undo (Ctrl/Cmd + Z)"
          style={{ marginRight: '0.5rem' }}
        >
          ↩ Undo
        </button>
        <button
          onClick={() => tableStore.redo()}
          disabled={!tableStore.canRedo}
          title="Redo (Ctrl/Cmd + Shift + Z)"
        >
          ↪ Redo
        </button>
      </div> */}
      <div className="c-editor-table-bg"></div>
      <div className="c-editor-table-container">
        <PanelGroup direction="horizontal" id="container">
          <Panel>
            <PanelGroup direction="vertical" id="layout">
              <Panel
                id="toolbar-panel"
                defaultSize={toolbarMinSize}
                minSize={toolbarMinSize}
                maxSize={toolbarMaxSize}
                className="c-editor-table-panel"
              >
                <ControlsBar
                  openSupervisorPanel={() =>
                    showSupervisorPanel(!supervisorPanelOpen)
                  }
                  supervisorPanelOpen={supervisorPanelOpen}
                >
                  <Search
                    size="medium"
                    style={{
                      width: 290,
                      marginBlock: -3,
                      marginInline: -19,
                    }}
                    placeholder="Search all columns..."
                    id="global-search"
                    // @ts-ignore
                    ref={globalSearch}
                    onfocus={() => {
                      tableStore.stopCellEditing();
                    }}
                    onInput={debounce((e) => {
                      setGlobalFilter(String(e.target?.value));
                      tableStore.search(String(e.target?.value));
                      tableStore.currentPage = 0;
                      // tableRef.current?.table.setGlobalFilter(
                      //   String(e.target?.value) || ''
                      // );
                    }, 200)}
                  ></Search>
                </ControlsBar>
              </Panel>
              <PanelResizeHandle
                className="c-editor-table-resizer"
                id="table-resizer"
              >
                <div className="c-editor-table-resizer__icon">
                  <img src={svgHandles as unknown as string} alt="" />
                </div>
              </PanelResizeHandle>
              <Panel
                id="content-panel"
                minSize={0}
                className="c-editor-table-panel"
              >
                <div className="c-editor-table-scroller" ref={tableScrollerRef}>
                  <div className="c-editor-table-wrapper">
                    <table
                      test-id="ps-editor-table"
                      id="ps-editor-table"
                      className={`c-editor-table ${tableStore.isEditing() ? 'c-editor-table--editing' : ''}`}
                      ref={tableRef}
                      {...listeners}
                    >
                      <thead className="c-editor-table__thead">
                        {table.getHeaderGroups().map((headerGroup) => (
                          <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                              if (header.id === 'index') {
                                return (
                                  <th
                                    test-id="c-editor-table--indexCell-th"
                                    ref={indexCellRef}
                                    key={header.id}
                                    className="c-editor-table__th-index-cell"
                                  >
                                    <span></span>
                                  </th>
                                );
                              }

                              return (
                                <HeaderCell
                                  key={header.id}
                                  header={header}
                                  // @ts-ignore
                                  hidden={header.column.columnDef.hidden}
                                />
                              );
                            })}
                          </tr>
                        ))}
                        {/* {table.getHeaderGroups().map((headerGroup) => (
                          <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => {
                              if (header.id === 'index')
                                return (
                                  <th
                                    ref={indexCellRef}
                                    key={header.id}
                                    className="c-editor-table__th-index-cell"
                                    test-id="c-editor-table--indexCell-th"
                                  >
                                    <span></span>
                                  </th>
                                );

                              return (
                                <HeaderCell key={header.id} header={header} />
                              );
                            })}
                          </tr>
                        ))} */}
                      </thead>

                      <tbody>
                        <DragDropProvider
                          onDragEnd={handleDragEnd}
                          onDragStart={handleDragStart}
                          onDragMove={handleDragMove}
                          sensors={[
                            PointerSensor.configure({
                              activationConstraints: [
                                new PointerActivationConstraints.Distance({
                                  value: 3,
                                }),
                              ],
                            }),
                            KeyboardSensor,
                          ]}
                        >
                          {table
                            .getRowModel()
                            .rows.filter((row) => row.original.hidden !== true)
                            .map((row) => {
                              const rowId = row.original.id;
                              const rowHasError = rowNodeNumbersWithErrors.has(
                                row.original.nodeNumber ?? null
                              );

                              return (
                                <DraggableRow
                                  key={rowId}
                                  rowId={rowId}
                                  ref={(el) => {
                                    if (el) {
                                      rowRefs.current.set(rowId, el);
                                    } else {
                                      rowRefs.current.delete(rowId);
                                    }
                                  }}
                                  hasError={rowHasError}
                                  onResizeStart={handleResizeStart}
                                  isResizing={resizingRowId === rowId}
                                  onResetSize={handleResetSize}
                                  currentResizeHeight={
                                    resizingRowId === rowId
                                      ? resizeHeight
                                      : null
                                  }
                                >
                                  {row.getVisibleCells().map((cell) => {
                                    const cellError = cellErrorsMap.get(
                                      `${row.original.nodeNumber ?? 'null'}-${cell.column.id}`
                                    );

                                    const key = getCellKey(
                                      cell.row.id,
                                      cell.column.id
                                    );

                                    return (
                                      <Cell
                                        key={cell.id}
                                        cell={cell}
                                        rowId={row.original.id}
                                        ref={(el) => {
                                          if (el) {
                                            cellRefs.current.set(key, el);
                                          } else {
                                            cellRefs.current.delete(key);
                                          }
                                        }}
                                        // @ts-ignore
                                        hidden={cell.column.columnDef.hidden}
                                        errorMessage={cellError?.errorMessage}
                                      />
                                    );
                                  })}
                                </DraggableRow>
                              );
                            })}
                        </DragDropProvider>
                      </tbody>
                    </table>

                    {resourceLibraryData?.length && (
                      <div className="c-editor-table__controls">
                        <Tooltip content="Add Node">
                          <IconButton
                            test-id="ps-editor-table-add-row-button"
                            size="small"
                            variant="new-primary"
                            name="plus"
                            onClick={() => tableStore.addEmptyRow()}
                          />
                        </Tooltip>
                        <hr className="c-editor-table__controls-divider" />
                        <Button
                          disabled={!resourceLibraryData?.length}
                          size="small"
                          variant="ghost"
                          onClick={() =>
                            showResourceLibPanel((state) => !state)
                          }
                        >
                          <Icon name="grid-menu" slot="prefix" size="small" />
                          Resource Library
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
                <div
                  test-id="ps-editor-table-info"
                  className="c-editor-table-footer c-editor-table-add-row-controls"
                >
                  <div className="c-editor-table-footer__th">
                    <div className="c-editor-table-footer__inner">
                      <Stack nowrap alignItems="center" gutter="xsmall">
                        <Button
                          test-id="ps-editor-table-add-row-button"
                          size="small"
                          variant="ghost"
                          onClick={() => tableStore.addEmptyRow()}
                          className="c-editor-table-add-row-control__btn"
                        >
                          <Icon slot="prefix" name="plus" size="xsmall" />
                          Add Node
                        </Button>

                        <Button
                          size="small"
                          variant="ghost"
                          onClick={() => tableStore.addEmptyRows(10)}
                        >
                          <Icon slot="prefix" name="plus" size="xsmall" />
                          Add 10 Nodes
                        </Button>

                        <Divider vertical />
                      </Stack>

                      <Stack
                        nowrap
                        alignItems="center"
                        gutter="small"
                        className="u-align-items-center"
                      >
                        <Button
                          size="small"
                          variant="ghost"
                          onClick={() => {
                            tableStore.pendingRefocusAfterPageChange = true;
                            table.firstPage();
                          }}
                          disabled={!table.getCanPreviousPage()}
                        >
                          {'<<'}
                        </Button>
                        <Button
                          size="small"
                          variant="ghost"
                          onClick={() => {
                            tableStore.pendingRefocusAfterPageChange = true;
                            table.previousPage();
                          }}
                          disabled={!table.getCanPreviousPage()}
                        >
                          {'<'}
                        </Button>
                        <Button
                          size="small"
                          variant="ghost"
                          onClick={() => {
                            tableStore.pendingRefocusAfterPageChange = true;
                            table.nextPage();
                          }}
                          disabled={!table.getCanNextPage()}
                        >
                          {'>'}
                        </Button>
                        <Button
                          size="small"
                          variant="ghost"
                          onClick={() => {
                            tableStore.pendingRefocusAfterPageChange = true;
                            table.lastPage();
                          }}
                          disabled={!table.getCanNextPage()}
                        >
                          {'>>'}
                        </Button>

                        {isDebugMode && (
                          <Stack
                            nowrap
                            alignItems="center"
                            gutter="small"
                            className="u-align-items-center"
                          >
                            <Divider vertical />
                            <Button
                              test-id="ps-editor-table-reset-order-button"
                              size="small"
                              variant="ghost"
                              onClick={() => tableStore.normalizeRowOrder()}
                              className="c-editor-table-add-row-control__btn"
                            >
                              Reset Row Order
                            </Button>

                            <Button
                              test-id="ps-editor-table-delete-all-nodes-button"
                              size="small"
                              variant="ghost"
                              onClick={() => tableStore.deleteAllRows()}
                              className="c-editor-table-add-row-control__btn"
                            >
                              Delete All Rows
                            </Button>
                          </Stack>
                        )}
                      </Stack>
                      {tableStore.hasPendingRowsToRender && (
                        <>
                          <Divider vertical />
                          <TextBody size="small" variant="primary">
                            Loading more nodes...
                          </TextBody>
                        </>
                      )}
                      <Stack
                        gutter="xsmall"
                        nowrap
                        className="u-margin-left-auto"
                      >
                        <TextSubtitle
                          test-id={`ps-editor-table-info-total-nodes`}
                          size="xsmall"
                          variant="secondary"
                          truncate
                        >
                          {table.getFilteredRowModel().rows.length !==
                            tableStore.rows.length &&
                            `Showing ${table.getFilteredRowModel().rows.length}${' '}
                              out of `}
                          {tableStore.nodeCounts.totalNodes} Nodes
                        </TextSubtitle>
                        <Divider vertical />
                        <TextBody
                          test-id={`ps-editor-table-info-decision-nodes`}
                          size="small"
                          variant="secondary"
                          truncate
                        >
                          {tableStore.nodeCounts.decisionNodes} Decision Nodes
                        </TextBody>
                        <TextBody
                          test-id={`ps-editor-table-info-action-nodes`}
                          size="small"
                          variant="secondary"
                          truncate
                        >
                          {tableStore.nodeCounts.actionNodes} Action Nodes
                        </TextBody>
                      </Stack>
                    </div>
                  </div>
                </div>
                {/** 
                <Info
                  testId="ps-editor-table-info"
                  projectName={projectName || ''}
                  botName={botName || ''}
                  decisionNodes={tableStore.nodeCounts.decisionNodes}
                  actionNodes={tableStore.nodeCounts.actionNodes}
                  totalNodes={tableStore.nodeCounts.totalNodes}
                />
                */}
              </Panel>
            </PanelGroup>
          </Panel>
          {/* sidebarOpen && (
                <Panel
                  defaultSize={sidebarSize}
                  minSize={sidebarSize}
                  maxSize={sidebarSize}
                  className="c-editor-table__layout-sidepanel"
                >
                  <EmbedPanel onClose={() => showSidebar(false)} />
                </Panel>
              ) */}
          {/* addEmbedsPanelOpen && (
                <Panel
                  defaultSize={sidebarSize}
                  minSize={sidebarSize}
                  maxSize={sidebarSize}
                  className="c-editor-table__layout-sidepanel"
                >
                  <AddEmbedsPanel onClose={() => showEmbedsPanel(false)} />
                </Panel>
              ) */}
          {supervisorPanelOpen && (
            <Panel
              defaultSize={sidebarSize}
              minSize={sidebarSize}
              maxSize={sidebarSize}
              className="c-editor-table__layout-sidepanel c-editor-table__layout-sidepanel--overflow-visible"
            >
              <SupervisorPanel onClose={() => showSupervisorPanel(false)} />
            </Panel>
          )}
          {resourceLibPanelOpen && (
            <Panel
              defaultSize={sidebarSize}
              minSize={sidebarSize}
              maxSize={sidebarSize}
              className="c-editor-table__layout-sidepanel"
            >
              <ResourceLibPanel
                resourceLibData={resourceLibraryData}
                onClose={() => showResourceLibPanel(false)}
              />
            </Panel>
          )}
        </PanelGroup>

        {/*
        <div className="c-editor-table-view-control">
          <Button
            test-id="ps-editor-table-view-control-btn"
            width="full"
            size="xlarge"
            variant="secondary"
            disabled={!tableStore.draftVersion || !tableStore.solutionVersionId}
            onClick={() =>
              tableStore.setState(
                tableStore.isEditing() ? 'viewing' : 'editing'
              )
            }
          >
            <Icon
              slot="prefix"
              name={tableStore.isEditing() ? 'unlock' : 'lock'}
            />
            {tableStore.isEditing() ? 'Edit Mode' : 'View Mode'}
          </Button>
        </div> */}
      </div>
    </>
  );
});
